// Canonical documentation source of truth: docs/MIGRATE_FROM_RESEND.md
// Synchronized React Documentation UI for CFMailOS Resend Migration

import { useState } from "react";
import { ArrowLeft, Check, Copy, Cpu, FileText, Layers, RefreshCw, ShieldCheck, Zap } from "lucide-react";
import { Link } from "react-router";
import { Button, Page } from "../components/ui";

const RESEND_COMPAT_AGENT_PROMPT = `Migrate this project's existing Resend email integration to CFMailOS using Resend Compatibility Mode.

Do not begin modifying files until you have completed the repository inspection
and identified the current Resend integration surface.

Before implementation, report whether the application can use the requested
migration mode safely.

If CFMailOS does not currently support a Resend feature that this application
depends on, do not silently remove or replace that behavior. Report the
compatibility gap and preserve the existing implementation until an equivalent
solution is available.

Inspect the entire repository and identify:

1. Every Resend SDK/package dependency.
2. Every Resend client initialization.
3. Every RESEND_API_KEY reference.
4. Any hardcoded Resend API URLs.
5. All email sending functions.
6. All batch-email functionality.
7. All webhook endpoints and handlers.
8. RESEND_WEBHOOK_SECRET or other webhook signing secrets.
9. Resend webhook signature verification.
10. Resend event types relied upon by the application.
11. Tests, mocks, fixtures, CI configuration, deployment configuration, and documentation related to Resend.

The goal is to route the existing Resend-compatible implementation through CFMailOS with the minimum necessary code changes.

Prefer keeping the existing Resend SDK and existing email sending code when compatible.

Configure:

RESEND_API_KEY=<CFMailOS API key>
RESEND_BASE_URL=<CFMailOS API base URL>

If the project's installed Resend SDK does not support RESEND_BASE_URL, configure the CFMailOS base URL explicitly through the SDK's supported baseUrl configuration or migrate the relevant HTTP calls appropriately.

Do not hardcode secrets.

For webhooks:

- Preserve the application's existing public webhook URL unless there is a technical reason to change it.
- The webhook URL must be registered in CFMailOS.
- Replace the old Resend signing secret with the CFMailOS webhook signing secret.
- Preserve the existing RESEND_WEBHOOK_SECRET variable name in Compatibility Mode if this allows the existing handler to work unchanged.
- Verify that the existing signature verification mechanism is compatible with CFMailOS before assuming no code changes are required.
- Verify all event names and payload fields used by the application against CFMailOS compatibility.

Do not unnecessarily rename Resend variables, imports, or application code in Compatibility Mode. The purpose of this mode is minimum-risk migration, not terminology cleanup.

After implementing the migration:

1. Run type checking.
2. Run linting.
3. Run the complete relevant test suite.
4. Run the production build.
5. Test an email send through CFMailOS.
6. Verify the request is reaching CFMailOS rather than api.resend.com.
7. Test webhook delivery.
8. Test webhook signature verification.
9. Verify the application's required webhook event processing.
10. Check that no production secrets were committed to source control.

Report:

- files changed
- configuration changes
- environment variables required
- whether application sending code changed
- whether webhook code changed
- tests performed
- any Resend functionality used by this project that CFMailOS does not currently support

Do not remove working functionality merely to complete the migration.`;

const NATIVE_CFMAILOS_AGENT_PROMPT = `Migrate this project's email infrastructure from Resend to the native CFMailOS API integration.

Do not begin modifying files until you have completed the repository inspection
and identified the current Resend integration surface.

Before implementation, report whether the application can use the requested
migration mode safely.

If CFMailOS does not currently support a Resend feature that this application
depends on, do not silently remove or replace that behavior. Report the
compatibility gap and preserve the existing implementation until an equivalent
solution is available.

First inspect the entire repository before making changes.

Find:

1. Resend dependencies.
2. Resend imports.
3. Resend client initialization.
4. RESEND_API_KEY.
5. RESEND_WEBHOOK_SECRET.
6. RESEND_BASE_URL or hardcoded Resend API URLs.
7. All email sending code.
8. Batch sending.
9. Webhook handlers.
10. Webhook signature verification.
11. Resend event types.
12. Resend-specific helpers, services, variables, types, comments, and configuration.
13. Tests, mocks, fixtures, CI configuration, deployment configuration, and documentation.

Replace the Resend integration with the native CFMailOS API documented by CFMailOS.

Use CFMailOS terminology for the migrated integration.

Environment variables should use CFMailOS naming:

CFMAILOS_API_KEY=<CFMailOS API key>
CFMAILOS_API_URL=<CFMailOS API base URL>
CFMAILOS_WEBHOOK_SECRET=<CFMailOS webhook signing secret>

Replace Resend-specific SDK initialization and imports with the native CFMailOS implementation.

Where appropriate rename internal variables such as:

resend -> cfmailos

and rename configuration references from RESEND_* to CFMAILOS_*.

Do not blindly rename historical database migrations, persisted public values, external URLs, or unrelated strings where doing so could break compatibility.

Preserve the application's current email behavior.

This includes, where currently used:

- sender
- recipient
- CC
- BCC
- reply-to
- subject
- text
- HTML
- attachments
- headers
- tags
- scheduled sending
- batch sending
- idempotency behavior
- webhook processing

Only migrate features that exist in the application. Do not add unnecessary functionality.

For webhooks:

- Preserve the application's current public webhook route unless changing it is necessary.
- Register that endpoint in CFMailOS.
- Replace Resend webhook verification with CFMailOS-native verification.
- Use CFMAILOS_WEBHOOK_SECRET.
- Preserve the application's event-handling behavior.
- Validate event payload differences before modifying business logic.

After migration:

1. Remove the Resend SDK dependency if nothing else uses it.
2. Remove unused RESEND_* environment variables.
3. Remove obsolete Resend-specific configuration.
4. Run type checking.
5. Run linting.
6. Run relevant tests.
7. Run the production build.
8. Send a real test email through CFMailOS.
9. Confirm the request reaches CFMailOS.
10. Test webhook delivery and signature verification.
11. Search the repository again for remaining Resend references and classify each remaining occurrence as intentional or obsolete.
12. Ensure no secret has been committed.

Finally provide a migration report containing:

- files changed
- dependencies added
- dependencies removed
- environment variables added
- environment variables removed
- email behavior verified
- webhook behavior verified
- tests/build results
- intentional remaining Resend references
- unsupported or behaviorally different features discovered

Do not claim successful migration until the application's actual sending and webhook paths have been verified.`;

export function MigrateFromResend({
  baseUrl,
  embedded = false,
  onSwitchToApiRef,
}: {
  baseUrl: string;
  embedded?: boolean;
  onSwitchToApiRef?: () => void;
}) {
  const [selectedMode, setSelectedMode] = useState<"all" | "compat" | "native">("all");
  const [copiedCompat, setCopiedCompat] = useState(false);
  const [copiedNative, setCopiedNative] = useState(false);

  const apiHost = baseUrl ? baseUrl.replace(/\/v1$/u, "") : "https://api.mail.automatorx.ai";
  const compatUrl = `${apiHost}/compat/resend`;
  const nativeUrl = `${apiHost}/v1`;

  const copyToClipboard = async (text: string, setCopied: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const content = (
    <div className="migration-page-content">
      {/* Architecture & Compatibility Principle Banner */}
      <div className="notice migration-notice">
        <ShieldCheck size={18} />
        <div>
          <strong>Architecture &amp; Compatibility Principle</strong>
          <p>
            CFMailOS is an independent product built on Cloudflare infrastructure. <strong>Resend Compatibility Mode</strong> routes Resend-style API payloads through CFMailOS's endpoint (<code>{compatUrl}</code>). Using the Resend SDK pointed at CFMailOS routes your requests to your CFMailOS workspace; it does not interact with Resend's services.
          </p>
        </div>
      </div>

      {/* Component Summary Comparison Table: What Changes? */}
      <section className="summary-table-section">
        <h2>What changes?</h2>
        <p className="section-desc">Quick side-by-side comparison of Compatibility Mode vs Native CFMailOS API.</p>
        <div className="diff-table-container">
          <table className="mapping-table">
            <thead>
              <tr>
                <th>Component</th>
                <th>Compatibility Mode</th>
                <th>Native CFMailOS API</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Resend SDK</strong></td>
                <td>Keep <code>resend</code> package</td>
                <td>Remove <code>resend</code> package</td>
              </tr>
              <tr>
                <td><strong>Sending code</strong></td>
                <td>Usually unchanged</td>
                <td>Replace with REST API / native calls</td>
              </tr>
              <tr>
                <td><strong>API key variable</strong></td>
                <td>Keep <code>RESEND_API_KEY</code>, use CFMailOS key</td>
                <td>Replace with <code>CFMAILOS_API_KEY</code></td>
              </tr>
              <tr>
                <td><strong>API base URL</strong></td>
                <td>Point to <code>{compatUrl}</code></td>
                <td>CFMailOS endpoint <code>{nativeUrl}</code></td>
              </tr>
              <tr>
                <td><strong>Webhook URL</strong></td>
                <td>Usually unchanged</td>
                <td>Usually unchanged</td>
              </tr>
              <tr>
                <td><strong>Webhook secret</strong></td>
                <td>Replace with CFMailOS secret</td>
                <td>Replace with CFMailOS secret</td>
              </tr>
              <tr>
                <td><strong>Variable naming</strong></td>
                <td>Resend-compatible</td>
                <td>CFMailOS-native</td>
              </tr>
              <tr>
                <td><strong>Best for</strong></td>
                <td>Existing Resend integrations</td>
                <td>New/clean integrations &amp; refactors</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Visual Migration Chooser */}
      <section className="migration-chooser-section">
        <h2>Choose your migration pathway</h2>
        <p className="section-desc">Resend Compatibility Mode is recommended for existing applications. Native CFMailOS API is best for new projects or complete refactors.</p>

        <div className="chooser-cards">
          {/* Card 1: Compatibility Mode (DEFAULT & RECOMMENDED for existing users) */}
          <div
            className={`chooser-card primary-recommendation ${selectedMode === "compat" ? "selected" : ""}`}
            onClick={() => setSelectedMode(selectedMode === "compat" ? "all" : "compat")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && setSelectedMode(selectedMode === "compat" ? "all" : "compat")}
          >
            <div className="card-badge badge-recommended-primary">
              <Zap size={14} />
              Recommended if you're already using Resend
            </div>
            <h3>Resend Compatibility Mode</h3>
            <p className="card-subtitle">Fastest migration · Minimal code changes</p>

            <div className="card-benefits">
              <strong>Best when:</strong>
              <ul>
                <li><Check size={14} /> The project already uses the Resend SDK</li>
                <li><Check size={14} /> Minimal code changes &amp; lowest risk are preferred</li>
                <li><Check size={14} /> You want the fastest path to routing mail via CFMailOS</li>
              </ul>
            </div>

            <div className="card-footer">
              <span>{selectedMode === "compat" ? "Showing Option 1 details" : "Click to view Option 1 details"}</span>
            </div>
          </div>

          {/* Card 2: Native CFMailOS API */}
          <div
            className={`chooser-card ${selectedMode === "native" ? "selected" : ""}`}
            onClick={() => setSelectedMode(selectedMode === "native" ? "all" : "native")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && setSelectedMode(selectedMode === "native" ? "all" : "native")}
          >
            <div className="card-badge badge-clean">
              <Layers size={14} />
              Clean CFMailOS integration
            </div>
            <h3>Native CFMailOS API</h3>
            <p className="card-subtitle">Best for new projects or complete migrations</p>

            <div className="card-benefits">
              <strong>Best when:</strong>
              <ul>
                <li><Check size={14} /> This is a new project or clean architecture refactor</li>
                <li><Check size={14} /> You want CFMailOS terminology everywhere</li>
                <li><Check size={14} /> You want to remove the Resend SDK dependency</li>
              </ul>
            </div>

            <div className="card-footer">
              <span>{selectedMode === "native" ? "Showing Option 2 details" : "Click to view Option 2 details"}</span>
            </div>
          </div>
        </div>

        {selectedMode !== "all" && (
          <div className="chooser-reset">
            <Button variant="quiet" onClick={() => setSelectedMode("all")}>
              <RefreshCw size={14} />
              Show both migration options
            </Button>
          </div>
        )}
      </section>

      {/* OPTION 1: Resend Compatibility Mode */}
      {(selectedMode === "all" || selectedMode === "compat") && (
        <section className="migration-option-section" id="option-1-compatibility">
          <header className="option-header">
            <span className="card-badge badge-recommended-primary">
              <Zap size={14} />
              Recommended if you're already using Resend
            </span>
            <h2>★ Option 1: Resend Compatibility Mode</h2>
            <p>Fastest migration · Minimal code changes</p>
          </header>

          <div className="option-body">
            <p className="option-intro">
              The application keeps the <code>resend</code> SDK and its existing Resend-style API calls, but requests are routed to CFMailOS instead of Resend. For supported Resend SDK versions, migration may only require changing environment variables.
            </p>

            <h3>1. Environment Variable Configuration</h3>
            <p>Point your existing Resend SDK instance to your CFMailOS API endpoint by configuring <code>RESEND_BASE_URL</code>:</p>

            <div className="code-block-container">
              <div className="code-header">
                <FileText size={14} />
                <span>.env</span>
              </div>
              <pre>{`# Configure your CFMailOS API key (created with Resend Compatibility mode)
RESEND_API_KEY=cfmo_live_...

# Route API requests to your CFMailOS Resend compatibility endpoint
RESEND_BASE_URL=${compatUrl}`}</pre>
            </div>

            <h3>2. Application Code</h3>
            <p>When using Resend Node SDK v6.19+ (or SDKs that automatically read <code>RESEND_BASE_URL</code>), existing code remains completely unchanged:</p>

            <div className="code-block-container">
              <div className="code-header">
                <FileText size={14} />
                <span>src/email.ts</span>
              </div>
              <pre>{`import { Resend } from "resend";

// Picks up RESEND_API_KEY and RESEND_BASE_URL automatically from environment
const resend = new Resend(process.env.RESEND_API_KEY);

await resend.emails.send({
  from: "hello@example.com",
  to: "customer@example.com",
  subject: "Hello from CFMailOS",
  html: "<p>Hello world</p>",
});`}</pre>
            </div>

            <div className="sdk-note">
              <strong>Explicit Base URL Configuration Alternative:</strong>
              <p>
                Not all Resend SDK versions or languages support environment-based base URL overrides. If your installed SDK version does not read <code>RESEND_BASE_URL</code> automatically, pass <code>baseUrl</code> explicitly in code:
              </p>
              <div className="code-block-container mini">
                <pre>{`const resend = new Resend(process.env.RESEND_API_KEY, {
  baseUrl: process.env.CFMAILOS_API_URL || "${compatUrl}",
});`}</pre>
              </div>
            </div>

            <h3>3. Webhooks in Compatibility Mode</h3>
            <p>Your existing public webhook endpoint URL normally does not need to change:</p>
            <div className="endpoint-pill">
              <code>https://example.com/api/webhooks/resend</code>
            </div>

            <ul className="instruction-list">
              <li>Register that existing URL as a webhook endpoint inside CFMailOS under <strong>Developer &gt; Webhooks</strong>.</li>
              <li>Set payload mode to <strong>Resend-compatible</strong>.</li>
              <li>
                Replace your previous Resend webhook signing secret with the signing secret generated by CFMailOS:
                <div className="code-block-container mini">
                  <pre>{`RESEND_WEBHOOK_SECRET=whsec_cfmailos_...`}</pre>
                </div>
              </li>
            </ul>

            <div className="notice verification-notice">
              <ShieldCheck size={16} />
              <div>
                <strong>Supported &amp; Verified vs Expected Compatibility</strong>
                <p>
                  Automated contract tests in CFMailOS verify core <code>emails.send()</code>, <code>emails.get()</code>, <code>emails.list()</code>, and HMAC signature creation. CFMailOS delivers webhooks signed with HMAC-SHA256 headers (<code>cfmailos-id</code>, <code>cfmailos-timestamp</code>, <code>cfmailos-signature</code>). If your webhook handler explicitly invokes Svix helpers expecting <code>svix-signature</code> headers, adjust the header verification checks while preserving event processing logic.
                </p>
              </div>
            </div>

            <h3>Verification Checklist (Compatibility Mode)</h3>
            <div className="checklist-box">
              <label className="check-item"><input type="checkbox" defaultChecked /> Created a CFMailOS API key with <strong>Migrate a Resend integration</strong> enabled</label>
              <label className="check-item"><input type="checkbox" defaultChecked /> Set <code>RESEND_API_KEY</code> and <code>RESEND_BASE_URL</code> environment variables</label>
              <label className="check-item"><input type="checkbox" defaultChecked /> Verify email requests hit CFMailOS (<code>/compat/resend/emails</code>) instead of <code>api.resend.com</code></label>
              <label className="check-item"><input type="checkbox" defaultChecked /> Register existing webhook URL in CFMailOS dashboard</label>
              <label className="check-item"><input type="checkbox" defaultChecked /> Update <code>RESEND_WEBHOOK_SECRET</code> with CFMailOS signing secret</label>
              <label className="check-item"><input type="checkbox" defaultChecked /> Test webhook delivery and signature verification</label>
            </div>

            {/* AI Agent Prompt Sub-Section */}
            <div className="agent-prompt-wrapper">
              <header className="agent-header">
                <div className="agent-title">
                  <Cpu size={18} />
                  <span>AI Agent Instruction Prompt: Resend Compatibility Mode</span>
                </div>
                <button
                  className={`copy-agent-btn ${copiedCompat ? "copied" : ""}`}
                  onClick={() => {
                    void copyToClipboard(RESEND_COMPAT_AGENT_PROMPT, setCopiedCompat);
                  }}
                >
                  {copiedCompat ? (
                    <>
                      <Check size={15} />
                      <strong>Copied</strong>
                    </>
                  ) : (
                    <>
                      <Copy size={15} />
                      <span>Copy AI Agent Instructions</span>
                    </>
                  )}
                </button>
              </header>

              <p className="agent-desc">
                Prefer using an AI coding agent? Copy the instruction below and paste it into your coding agent (Codex, Claude Code, Cursor, etc.) while it has access to your project.
              </p>

              <div className="agent-prompt-box">
                <pre>{RESEND_COMPAT_AGENT_PROMPT}</pre>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* OPTION 2: Native CFMailOS API */}
      {(selectedMode === "all" || selectedMode === "native") && (
        <section className="migration-option-section" id="option-2-native">
          <header className="option-header">
            <span className="card-badge badge-clean">
              <Layers size={14} />
              Clean CFMailOS integration
            </span>
            <h2>Option 2: Native CFMailOS API</h2>
            <p>Clean CFMailOS integration · Best for new projects or complete migrations</p>
          </header>

          <div className="option-body">
            <p className="option-intro">
              Use CFMailOS directly through its REST API. Official standalone CFMailOS SDKs are planned but not yet released. In TypeScript/Node workspaces, <code>@cfmailos/sdk</code> is available.
            </p>

            <h3>1. Environment Variables</h3>
            <p>Replace Resend environment variables with native CFMailOS variables:</p>

            <div className="diff-table-container">
              <table className="mapping-table">
                <thead>
                  <tr>
                    <th>Before (Resend)</th>
                    <th>After (Native CFMailOS API)</th>
                    <th>Example</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>RESEND_API_KEY</code></td>
                    <td><code>CFMAILOS_API_KEY</code></td>
                    <td><code>cfmo_live_...</code></td>
                  </tr>
                  <tr>
                    <td><code>RESEND_BASE_URL</code></td>
                    <td><code>CFMAILOS_API_URL</code></td>
                    <td><code>{nativeUrl}</code></td>
                  </tr>
                  <tr>
                    <td><code>RESEND_WEBHOOK_SECRET</code></td>
                    <td><code>CFMAILOS_WEBHOOK_SECRET</code></td>
                    <td><code>whsec_...</code></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3>2. Direct REST API Call Example</h3>
            <div className="code-block-container">
              <div className="code-header">
                <FileText size={14} />
                <span>cURL / HTTP Request</span>
              </div>
              <pre>{`curl -X POST "${nativeUrl}/emails" \\
  -H "Authorization: Bearer cfmo_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "from": "notifications@send.example.com",
    "to": "user@example.com",
    "subject": "Welcome to CFMailOS",
    "html": "<p>Hello from native CFMailOS API</p>"
  }'`}</pre>
            </div>

            <h3>3. TypeScript / Node Workspace Code</h3>

            <div className="code-comparison-grid">
              <div className="comparison-col">
                <span className="col-label label-before">Before: Resend SDK</span>
                <pre>{`import { Resend } from "resend";

const resend = new Resend(process.env.RESEND_API_KEY);

const { data, error } = await resend.emails.send({
  from: "hello@example.com",
  to: "customer@example.com",
  subject: "Hello",
  html: "<p>Hello</p>",
});`}</pre>
              </div>

              <div className="comparison-col">
                <span className="col-label label-after">After: Native @cfmailos/sdk</span>
                <pre>{`import { CFMailOS } from "@cfmailos/sdk";

const cfmailos = new CFMailOS({
  apiKey: process.env.CFMAILOS_API_KEY!,
  baseUrl: process.env.CFMAILOS_API_URL || "${nativeUrl}",
});

const { data, error } = await cfmailos.emails.send({
  from: "hello@example.com",
  to: "customer@example.com",
  subject: "Hello",
  html: "<p>Hello</p>",
});`}</pre>
              </div>
            </div>

            <h3>4. Webhooks &amp; Signature Verification</h3>
            <p>Register your endpoint with <strong>CFMailOS native</strong> payload mode and use the native signature verification helper:</p>

            <div className="code-block-container">
              <div className="code-header">
                <FileText size={14} />
                <span>api/webhooks/route.ts</span>
              </div>
              <pre>{`import { verify } from "@cfmailos/sdk";

export async function POST(req: Request) {
  const rawBody = await req.text();
  
  try {
    const event = await verify({
      payload: rawBody,
      headers: req.headers,
      secret: process.env.CFMAILOS_WEBHOOK_SECRET!,
    });

    if (event.type === "email.delivered") {
      console.log("Email delivered:", event.data);
    }
    return new Response("OK", { status: 200 });
  } catch (err) {
    return new Response("Invalid signature", { status: 400 });
  }
}`}</pre>
            </div>

            <h3>Verification Checklist (Native API)</h3>
            <div className="checklist-box">
              <label className="check-item"><input type="checkbox" defaultChecked /> Created native CFMailOS API key in dashboard</label>
              <label className="check-item"><input type="checkbox" defaultChecked /> Replaced environment variables (<code>CFMAILOS_API_KEY</code>, <code>CFMAILOS_API_URL</code>, <code>CFMAILOS_WEBHOOK_SECRET</code>)</label>
              <label className="check-item"><input type="checkbox" defaultChecked /> Refactored SDK imports and client instances (<code>resend</code> -&gt; <code>cfmailos</code>)</label>
              <label className="check-item"><input type="checkbox" defaultChecked /> Updated webhook handlers to use CFMailOS signature verification</label>
              <label className="check-item"><input type="checkbox" defaultChecked /> Removed unused <code>resend</code> package dependency</label>
              <label className="check-item"><input type="checkbox" defaultChecked /> Executed typechecking, linting, unit tests, and production build</label>
              <label className="check-item"><input type="checkbox" defaultChecked /> Confirmed email sending reaches CFMailOS <code>/v1/emails</code> endpoint</label>
            </div>

            {/* AI Agent Prompt Sub-Section */}
            <div className="agent-prompt-wrapper">
              <header className="agent-header">
                <div className="agent-title">
                  <Cpu size={18} />
                  <span>AI Agent Instruction Prompt: Native CFMailOS API Migration</span>
                </div>
                <button
                  className={`copy-agent-btn ${copiedNative ? "copied" : ""}`}
                  onClick={() => {
                    void copyToClipboard(NATIVE_CFMAILOS_AGENT_PROMPT, setCopiedNative);
                  }}
                >
                  {copiedNative ? (
                    <>
                      <Check size={15} />
                      <strong>Copied</strong>
                    </>
                  ) : (
                    <>
                      <Copy size={15} />
                      <span>Copy AI Agent Instructions</span>
                    </>
                  )}
                </button>
              </header>

              <p className="agent-desc">
                Prefer using an AI coding agent? Copy the instruction below and paste it into your coding agent (Codex, Claude Code, Cursor, etc.) while it has access to your project.
              </p>

              <div className="agent-prompt-box">
                <pre>{NATIVE_CFMAILOS_AGENT_PROMPT}</pre>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <Page
      eyebrow="Developer / migration"
      title="Migrate from Resend"
      description="Step-by-step instructions and AI agent prompts for migrating an existing Resend email integration to CFMailOS."
      action={
        onSwitchToApiRef ? (
          <Button variant="secondary" onClick={onSwitchToApiRef}>
            <ArrowLeft size={15} />
            API Reference
          </Button>
        ) : (
          <Link to="/developer/docs">
            <Button variant="secondary">
              <ArrowLeft size={15} />
              API Docs
            </Button>
          </Link>
        )
      }
    >
      {content}
    </Page>
  );
}
